import { useState, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { Upload, Link as LinkIcon, Clock, Shield, Lock } from 'lucide-react'
import { Dropzone } from '@/components/upload/Dropzone'
import { UploadProgress } from '@/components/upload/UploadProgress'
import { UploadComplete } from '@/components/upload/UploadComplete'
import { fileService } from '@/services/files'
import { useToastStore } from '@/store/toastStore'
import { getErrorMessage } from '@/utils/errors'
import { trackEvent } from '@/utils/analytics'
import {
  generateEncryptionKey,
  exportKeyToString,
  encryptFile,
  buildEncryptedShareURL,
} from '@/utils/crypto'
import type { UploadResponse, EncryptionMetadata } from '@/types'

type UploadState = 'idle' | 'encrypting' | 'uploading' | 'complete'

export function Home() {
  const [uploadState, setUploadState] = useState<UploadState>('idle')
  const [progress, setProgress] = useState(0)
  const [uploadResult, setUploadResult] = useState<UploadResponse | null>(null)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const addToast = useToastStore((state) => state.addToast)
  const abortControllerRef = useRef<AbortController | null>(null)

  const handleFilesSelected = (files: File[]) => {
    setSelectedFiles(files)
  }

  const handleUpload = async (options?: { password?: string; maxDownloads?: number; encrypted?: boolean }) => {
    if (selectedFiles.length === 0) return

    trackEvent('upload_started', { file_count: selectedFiles.length, encrypted: !!options?.encrypted })

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    try {
      let filesToUpload = selectedFiles
      let encryptionMetadatas: EncryptionMetadata[] | undefined
      let keyString: string | undefined

      if (options?.encrypted) {
        // Encryption phase
        setUploadState('encrypting')
        setProgress(0)

        const key = await generateEncryptionKey()
        keyString = await exportKeyToString(key)

        const encryptedFiles: File[] = []
        encryptionMetadatas = []

        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i]
          const { encryptedBlob, metadata } = await encryptFile(file, key, (p) => {
            const overall = Math.round(((i + p / 100) / selectedFiles.length) * 100)
            setProgress(overall)
          })

          encryptedFiles.push(new File([encryptedBlob], file.name, { type: 'application/octet-stream' }))
          encryptionMetadatas.push(metadata)
        }

        filesToUpload = encryptedFiles
        setProgress(100)
      }

      // Upload phase
      setUploadState('uploading')
      setProgress(0)

      const result = await fileService.upload(filesToUpload, {
        password: options?.password,
        maxDownloads: options?.maxDownloads,
        onProgress: (percent) => setProgress(percent),
        signal: abortController.signal,
        isEncrypted: options?.encrypted,
        encryptionMetadata: encryptionMetadatas,
      })

      // If encrypted, append #key= to share links
      if (options?.encrypted && keyString) {
        result.share_link = buildEncryptedShareURL(result.share_link, keyString)
        if (result.share_links) {
          result.share_links = result.share_links.map((link) =>
            buildEncryptedShareURL(link, keyString!)
          )
        }
      }

      setProgress(100)
      setUploadResult(result)
      setUploadState('complete')
      trackEvent('upload_completed', { file_count: selectedFiles.length, encrypted: !!options?.encrypted })
      addToast('success', 'Files uploaded successfully!')
    } catch (error) {
      if (abortController.signal.aborted) {
        return
      }
      setUploadState('idle')
      addToast('error', getErrorMessage(error, 'Upload failed. Please try again.'))
      console.error('Upload error:', error)
    } finally {
      abortControllerRef.current = null
    }
  }

  const handleCancel = () => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    setUploadState('idle')
    setProgress(0)
    addToast('info', 'Upload cancelled.')
  }

  const handleReset = () => {
    setUploadState('idle')
    setProgress(0)
    setUploadResult(null)
    setSelectedFiles([])
  }

  return (
    <div className="min-h-[calc(100vh-8rem)] flex flex-col items-center justify-center px-4 py-12">
      <Helmet>
        <title>VaultLynk — Secure File Sharing with Expiring Links</title>
        <meta name="description" content="Share files with auto-expiring links and password protection. No sign-up required — just drag, drop, and share." />
        <meta property="og:title" content="VaultLynk — Secure File Sharing" />
        <meta property="og:description" content="Share files with auto-expiring links and password protection. No sign-up required." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://vaultlynk.com" />
        <meta property="og:image" content="https://vaultlynk.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="VaultLynk — Secure File Sharing" />
        <meta name="twitter:description" content="Share files with auto-expiring links and password protection." />
        <meta name="twitter:image" content="https://vaultlynk.com/og-image.png" />
      </Helmet>

      <div className="max-w-3xl w-full text-center mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          Share files <span className="text-gradient">securely</span>
        </h1>
        <p className="text-lg text-gray-600 mb-4">
          Secure file sharing with auto-expiring links. No sign-up required — just drag, drop, and share.
        </p>
      </div>

      <div id="dropzone" className="w-full max-w-2xl">
        {uploadState === 'idle' && (
          <Dropzone
            onFilesSelected={handleFilesSelected}
            onUpload={handleUpload}
            selectedFiles={selectedFiles}
          />
        )}

        {(uploadState === 'encrypting' || uploadState === 'uploading') && (
          <UploadProgress
            files={selectedFiles}
            progress={progress}
            phase={uploadState}
            onCancel={handleCancel}
          />
        )}

        {uploadState === 'complete' && uploadResult && (
          <UploadComplete
            result={uploadResult}
            onReset={handleReset}
          />
        )}
      </div>

      {/* How it works */}
      <div className="mt-16 max-w-4xl w-full">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">How it works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Upload className="w-7 h-7 text-primary-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Upload your files</h3>
            <p className="text-sm text-gray-600">
              Drag and drop or browse to select your files. Up to 50MB free.
            </p>
          </div>

          <div className="text-center">
            <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <LinkIcon className="w-7 h-7 text-primary-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Get a share link</h3>
            <p className="text-sm text-gray-600">
              Instantly get a unique link to share with anyone. Add a password for extra security.
            </p>
          </div>

          <div className="text-center">
            <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-7 h-7 text-primary-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">It expires automatically</h3>
            <p className="text-sm text-gray-600">
              Your link and files are automatically deleted after the expiry period. No traces left behind.
            </p>
          </div>
        </div>
      </div>

      {/* Trust badges */}
      <div className="mt-12 max-w-3xl w-full">
        <div className="flex flex-wrap items-center justify-center gap-8 text-gray-500">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-gray-400" />
            <span className="text-sm">End-to-end encryption available</span>
          </div>
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-gray-400" />
            <span className="text-sm">HTTPS secured</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-400" />
            <span className="text-sm">Auto-expiring links</span>
          </div>
        </div>
      </div>

    </div>
  )
}
